import { Component, Input, EventEmitter, Output, ChangeDetectorRef, OnDestroy, inject } from '@angular/core';
import { DamageDetails, CustomerEquipmentUpgradeAgreement } from '../../../../models/equipment-upgrade-agreement.model';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GradingQuestion, InsuranceEligibility, QuestionAnswerPair } from '../../../../models/equipment-api.model';
import { catchError, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { EquipmentService } from '@sales/services/equipment.service';
import { NotificationService } from '@core/services/notification.service';
import { MobilePhoneInsuranceComponent } from '../mobile-phone-insurance/mobile-phone-insurance.component';
import { MobilePhoneGradingComponent } from '../mobile-phone-grading/mobile-phone-grading.component';
import { ReturnMobilePhoneDetailsComponent } from '../return-mobile-phone-details/return-mobile-phone-details.component';

enum Messages {
  ErrorOnInsurance = 'En internfeil har oppstått. Prøv på nytt senere.',
}

@Component({
  selector: 'app-old-mobile-phone-details',
  templateUrl: './old-mobile-phone-details.component.html',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ReturnMobilePhoneDetailsComponent,
    MobilePhoneGradingComponent,
    MobilePhoneInsuranceComponent,
  ],
})
export class OldMobilePhoneDetailsComponent implements OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private formBuilder = inject(FormBuilder);
  private equipmentService = inject(EquipmentService);
  private notificationService = inject(NotificationService);

  @Input() equipmentUpgradeAgreement: CustomerEquipmentUpgradeAgreement;
  @Input() canSellHardwareWithLogisticsClaim: boolean;
  @Input() gradingQuestions: GradingQuestion[];
  @Input() customerEmail: string;
  @Output() gradingCompleted = new EventEmitter();

  oldMobileForm = this.formBuilder.group({}) as FormGroup;
  oldMobilePhoneStep = 1;
  numberOfSteps = 2;
  redemptionId: number;
  insuranceEligibility: InsuranceEligibility;
  isLoadingGradingAnswers: boolean;
  isCreatingClaim: boolean;
  subscriptions$: Subscription[] = [];

  onSetRedemptionId(redemptionId: number) {
    this.redemptionId = redemptionId;
    if (!redemptionId) {
      this.insuranceEligibility = null;
    }
  }

  onSendGradingAnswers(gradingAnswers: QuestionAnswerPair[]) {
    this.isLoadingGradingAnswers = true;
    this.subscriptions$.push(
      this.equipmentService
        .sendGradingAnswers(this.redemptionId, this.equipmentUpgradeAgreement.activeImei, gradingAnswers)
        .pipe(
          tap((insuranceEligibilityResponse: InsuranceEligibility) => {
            this.insuranceEligibility = insuranceEligibilityResponse;
            this.isLoadingGradingAnswers = false;
            if (insuranceEligibilityResponse.mustUseInsurance) {
              this.numberOfSteps = 3;
              this.oldMobilePhoneStep = 3;
            } else {
              this.numberOfSteps = 2;
            }
            this.changeDetectorRef.detectChanges();
          }),
          catchError(error => {
            this.isLoadingGradingAnswers = false;

            this.notificationService.error(Messages.ErrorOnInsurance);
            throw error;
          })
        )
        .subscribe()
    );
  }

  onUseInsurance({ damageDate, damageReasonText, damageReason }) {
    const damageDetails = {
      damageDate,
      damageReasonText,
      damageReason,
    } as DamageDetails;
    const mobileReturnMethod = this.getMobileReturnMethod();
    // const returnAddress = mobileReturnMethod === MobileReturnMethod.ReturnPackage ? this.getReturnAddress() : null;

    this.isCreatingClaim = true;
    this.subscriptions$.push(
      this.equipmentService
        .createRedemptionClaim(
          this.redemptionId,
          this.equipmentUpgradeAgreement.activeImei,
          this.customerEmail,
          damageDetails
        )
        .pipe(
          tap(response => {
            this.isCreatingClaim = false;
            if (response.isUserAllowedToUseInsurance) {
              this.gradingCompleted.emit({
                redemptionId: this.redemptionId,
                useInsurance: true,
                claimId: response.claimId,
                damageDetails: damageDetails,
                mobileReturnMethod,
                // returnAddress,
                insuranceEligibility: this.insuranceEligibility,
              });
            } else {
              this.notificationService.error(Messages.ErrorOnInsurance);
            }
          }),
          catchError(error => {
            this.isCreatingClaim = false;
            this.notificationService.error(Messages.ErrorOnInsurance);
            throw error;
          })
        )
        .subscribe()
    );
  }

  onGradingCompleted() {
    const mobileReturnMethod = this.getMobileReturnMethod();
    this.gradingCompleted.emit({
      redemptionId: this.redemptionId,
      useInsurance: false,
      claimId: null,
      damageDetails: null,
      mobileReturnMethod,
      // returnAddress: mobileReturnMethod === MobileReturnMethod.ReturnPackage ? this.getReturnAddress() : null,
      insuranceEligibility: this.insuranceEligibility,
    });
  }

  private getMobileReturnMethod() {
    const formGroup = this.oldMobileForm.controls.returnGroup as FormGroup;
    return formGroup.controls.returnMethodControl.value;
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
